import {
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useMediaQuery,
  useStyleConfig,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { BiChevronDown } from "react-icons/bi";
import { useRouteManager } from "../wrappers/routes";
import { Link, useLocation, useNavigate } from "react-router";
import { TbBorderCornerRounded } from "react-icons/tb";
import { AnimatePresence, motion } from "framer-motion";
import useUserStore from "../store/userStore";
import { useFirebase } from "../wrappers/firebase";
import { signOut } from "firebase/auth";
import { shallow } from "zustand/shallow";
import { Tooltip } from "./reusables";
import useAppStore from "../store/appStore";
import SiteInfo from "../site-info";
import appIcon from "../assets/app_icon.png";
import CreateButton from "./sharing/createButton";
import { useStripe } from "../wrappers/stripe";

const MotionVSTack = motion.create(VStack);
export const NavButton = ({
  icon,
  title,
  nestedRoutes,
  path,
  buttonProps,
  defaultExpanded,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const styles = useStyleConfig("Button", { variant: "glow" });
  const navExpanded = useAppStore((state) => state.navExpanded, shallow);

  const isActive = () => {
    if (path === "/") return pathname === path;
    else return pathname.startsWith(path);
  };

  useEffect(() => {
    autoExpand();
  }, [pathname]);

  function autoExpand() {
    if (nestedRoutes?.find((nested) => nested.path === pathname)) {
      setExpanded(true);
    }
  }

  function goToPage() {
    navigate(path);
  }

  function toggleExpand(e) {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }
  return (
    <Tooltip placement="right" label={!navExpanded && title}>
      <VStack w="full" h="max-content" spacing={"0"}>
        <HStack
          w="full"
          variant={"nav"}
          as={Button}
          justify={navExpanded ? "space-between" : "flex-start"}
          // h={!navExpanded && "40px"}
          h="40px"
          bg={isActive() ? (!navExpanded ? "white" : "auto") : "transparent"}
          color={isActive() ? (navExpanded ? "white" : "black") : "white"}
          _hover={{
            color: "white",
          }}
          onClick={goToPage}
          {...buttonProps}
        >
          <HStack>
            <Box fontSize={"15px"}>{icon}</Box>
            {navExpanded && <Text>{title}</Text>}
          </HStack>
          {nestedRoutes && navExpanded && (
            <VStack
              __css={styles}
              onClick={toggleExpand}
              variant={"glow"}
              px="1.5"
              h="full"
              justify={"center"}
              align={"center"}
              cursor={"pointer"}
            >
              <BiChevronDown
                size="14px"
                style={{
                  transition: "all 150ms",
                  transform: `rotate(${expanded ? "180" : "0"}deg)`,
                  transformOrigin: "center",
                }}
              />
            </VStack>
          )}
        </HStack>
        {nestedRoutes && (
          <VStack w="full" overflow={"hidden"}>
            <AnimatePresence mode="wait" initial={false}>
              {expanded && (
                <MotionVSTack
                  w="full"
                  spacing={"0"}
                  pos="relative"
                  pl="1"
                  initial={{ opacity: 0, height: 0, y: -50 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -50 }}
                  transition={{ duration: 0.15, ease: "easeInOut" }}
                >
                  <Box
                    pos="absolute"
                    left="1.5"
                    top="0px"
                    bottom="16px"
                    w="1px"
                    bg="#3e3e46"
                  />
                  {nestedRoutes?.map((route, index) => (
                    <HStack
                      w="full"
                      justify={"flex-start"}
                      spacing={"0"}
                      key={`parent_route_${path}_child_${route.path}`}
                    >
                      <TbBorderCornerRounded
                        color="#3e3e46"
                        style={{
                          transform: `rotate(-90deg)`,
                          transformOrigin: "center",
                          marginTop: "-5px",
                        }}
                      />
                      <NavButton
                        {...route}
                        buttonProps={{
                          pl: 1.5,
                        }}
                      />
                    </HStack>
                  ))}
                </MotionVSTack>
              )}
            </AnimatePresence>
          </VStack>
        )}
      </VStack>
    </Tooltip>
  );
};

/**
 *
 * @param {{customRoutes?: Array, allExpanded?: boolean}} props
 * @returns
 */
const Navbar = ({ customRoutes, allExpanded, children }) => {
  const { AppRoutes } = useRouteManager();
  const _routes = customRoutes || AppRoutes;
  return (
    <VStack role="navigation" w="full" spacing={"1"}>
      {_routes
        .filter((route) => !route.ignoreRendering && route.isNativeRoute)
        .map((route, index) => (
          <NavButton
            defaultExpanded={allExpanded}
            key={`main_nav_${route.path}`}
            {...route}
          />
        ))}
      <CreateButton />
      <Box h="1" />
      {children}
    </VStack>
  );
};

const SidebarContent = ({ children }) => {
  const [isMobile] = useMediaQuery("(max-width: 600px)");
  const navExpanded = useAppStore((state) => state.navExpanded, shallow);
  const { LockedFeature } = useStripe();

  return (
    <Grid
      transition={"all 300ms"}
      w={isMobile ? "full" : "250px"}
      // bg={!isMobile ? (navExpanded ? "transparent" : "#2e2e30") : "#212123"}
      h={isMobile ? "max-content" : "full"}
      px={!isMobile && "10px"}
      py="3.5"
      gridTemplateRows={!isMobile && "55px 1fr"}
      gridRowGap={"2"}
      minH={"0"}
      mb={isMobile && "2"}
    >
      <Grid
        h="full"
        gridTemplateColumns={"25px auto auto"}
        gridRowGap={"0"}
        gridColumnGap={"3"}
        alignItems={"center"}
        alignContent={"center"}
        alignSelf={"flex-start"}
      >
        <Box gridArea={"1 / 1 / 2 / 2"}>
          <Image
            alt="app_icon"
            src={appIcon}
            w={"full"}
            // bg="white"
            borderRadius={"8px"}
            // p="2.5"
          />
        </Box>
        <VStack
          gridArea={"1 / 2 / 2 / 3"}
          spacing={"0"}
          align={"flex-start"}
          justify={"center"}
        >
          <Text
            fontSize={isMobile ? "lg" : "13px"}
            fontWeight={"medium"}
            noOfLines={1}
            as={Link}
            to="/"
          >
            Tarara.app
          </Text>
          {!isMobile && (
            <Text
              fontSize={"11px"}
              fontWeight={"medium"}
              noOfLines={1}
              color={"gray.400"}
            >
              Connecting artists with fans
            </Text>
          )}
        </VStack>
        {isMobile && (
          <VStack align={"flex-end"} justify={"center"}>
            <LockedFeature>
              <Button size="sm">Upgrade</Button>
            </LockedFeature>
          </VStack>
        )}
      </Grid>
      {!isMobile && (
        <VStack
          w="full"
          h="full"
          overflowY={"scroll"}
          overflowX={"hidden"}
          // borderTop={"1px solid #3e3e4660"}
          pt="2"
        >
          <Navbar allExpanded={isMobile}>{children}</Navbar>
        </VStack>
      )}
    </Grid>
  );
};

/**
 *
 * @param {import("@chakra-ui/react").BoxProps} props
 * @returns
 */
const MenuSidebar = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const [isMobile] = useMediaQuery("(max-width: 600px)");

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const toggleModal = () => setIsOpen((prev) => !prev);

  return (
    <>
      <Box {...props}>
        <SidebarContent>{props.children}</SidebarContent>
      </Box>
    </>
  );
};

export default MenuSidebar;
